/*
 * Wishlist King (Swish), started on intent instead of at load. See
 * snippets/wishlist-loader.liquid and .docs/integrations/swish-wishlist.md.
 *
 * Every Swish module script (app settings, collection buttons, code access,
 * wishlist page, the product page app block) does nothing unless
 * window.WishlistKingAppLoaderURL is set, which the app settings embed does
 * inline. This script is deferred from the head, so it runs after that line
 * and before any of those modules: it holds the URL back, then hands it over
 * on the shopper's first interaction, a hover or focus on a card or a heart,
 * or once the page has loaded and gone quiet. Swish's own block modules are
 * then imported again, so the app runs its current code, unchanged.
 *
 * Until then the theme draws a static heart where the app draws its own. A
 * tap on one starts the app and is passed on to the app's button once it has
 * rendered, so the first tap still saves the product.
 *
 * Fails safe: if the app stops reading that global, nothing is held back and
 * it loads as it always did.
 */
(() => {
  if (window.RokaWishlist) return;

  const loaderUrl = window.WishlistKingAppLoaderURL;

  if (!loaderUrl) return;

  const BLOCK_SCRIPTS =
    'script[type="module"][src*="/swish-wishlist-king"][src*="/assets/block-"]';
  const CARD_HEART = ".product-card__wishlist";
  const PAGE_HEART = "wishlist-button-block:not([wk-loaded])";
  const INTENT_TARGETS =
    ".product-card__media, wishlist-button-block, [data-wishlist-link]";
  /* The same interactions and quiet-after-load timing as
     assets/lipscore-loader.js, which defers Lipscore the same way. Two
     vendors, two files; change the timing in both. */
  const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"];
  const QUIET_AFTER_LOAD_MS = 3000;
  const IDLE_TIMEOUT_MS = 2000;
  /* A slow third-party file can hold the load event back for minutes on a
     poor connection, so the quiet wait starts by this point regardless. */
  const LOAD_WAIT_CAP_MS = 6000;
  const REPLAY_POLL_MS = 100;
  /* How long a tap or focus on a static heart waits for the app's button. On
     a slow connection the app takes 8 to 11 seconds to draw its buttons after
     it starts (measured on 12 Oct 2026), so this leaves room to spare. */
  const REPLAY_TIMEOUT_MS = 30000;

  const readWishlistPath = () => {
    try {
      const settings = JSON.parse(
        document.getElementById("wkAppSettings")?.textContent || "{}",
      );

      return settings.settings?.general?.wishlistPath || "/apps/wishlist";
    } catch {
      return "/apps/wishlist";
    }
  };

  /* The wishlist page is the app, and the theme editor previews it. */
  if (window.Shopify?.designMode) return;
  if (window.location.pathname.includes(readWishlistPath())) return;

  delete window.WishlistKingAppLoaderURL;

  /* Not removable means it was declared another way: leave the app alone. */
  if ("WishlistKingAppLoaderURL" in window) return;

  window.RokaWishlist = { loaderUrl };

  /* The static hearts draw the custom icon set in Swish admin → Code Access
     (Roka London). A store without one gets the app's default heart, which
     they would not match, so the static hearts stay hidden there and the
     app's own hearts appear when it starts. */
  const hasCustomIcons = (() => {
    try {
      const themeCode = JSON.parse(
        document.getElementById("wkThemeCode")?.textContent || "{}",
      );

      return Boolean(themeCode.customIconsUrl);
    } catch {
      return false;
    }
  })();

  if (hasCustomIcons) {
    document.documentElement.classList.add("has-wishlist");
  }

  let started = false;
  let quietTimer = 0;
  let loadCapTimer = 0;
  /* Deferred from the head, this always runs before DOMContentLoaded. */
  let parsed = document.readyState === "complete";
  const pendingTaps = new WeakSet();
  const pendingFocus = new WeakSet();

  /* Swish's own module scripts run after this one, once each has downloaded,
     and the browser handles input while it waits for them. Handing the URL
     back before they have all run would let them start the app as well as
     the copies imported below. DOMContentLoaded fires once they have run. */
  const handOver = () => {
    window.WishlistKingAppLoaderURL = loaderUrl;

    /* A module runs once per URL, so the query makes Swish's blocks run
       again, now that the URL is set. */
    for (const script of document.querySelectorAll(BLOCK_SCRIPTS)) {
      const url = new URL(script.src, window.location.href);

      url.searchParams.set("deferred", "1");
      import(url.href).catch(() => {});
    }
  };

  document.addEventListener(
    "DOMContentLoaded",
    () => {
      parsed = true;

      if (started) handOver();
    },
    { once: true },
  );

  const onIntent = (event) => {
    if (
      event.target instanceof Element &&
      event.target.closest(INTENT_TARGETS)
    ) {
      start();
    }
  };

  function start() {
    if (started) return;

    started = true;
    window.clearTimeout(quietTimer);
    window.clearTimeout(loadCapTimer);
    window.removeEventListener("load", waitForQuiet);

    for (const type of INTERACTION_EVENTS) {
      window.removeEventListener(type, start, { capture: true });
    }

    document.removeEventListener("pointerover", onIntent, { capture: true });
    document.removeEventListener("focusin", onIntent, { capture: true });

    if (parsed) handOver();
  }

  /* Wait for the app's own button to render, then act on it. One pending
     wait per heart and purpose, so a double tap cannot add and then remove. */
  const whenRendered = (pending, key, findButton, act) => {
    if (pending.has(key)) return;

    pending.add(key);

    const deadline = performance.now() + REPLAY_TIMEOUT_MS;
    const poll = () => {
      const button = findButton();

      if (button) {
        pending.delete(key);
        act(button);
        return;
      }

      if (performance.now() < deadline) {
        window.setTimeout(poll, REPLAY_POLL_MS);
        return;
      }

      pending.delete(key);
    };

    poll();
  };

  const cardButton = (media) =>
    media?.querySelector("wishlist-button-collection .wk-button") || null;

  /* The static heart reads "Add to wishlist", so a passed-on tap only ever
     adds: a product the shopper had already saved stays saved. */
  const addOnce = (button) => {
    if (!button.classList.contains("wk-selected")) button.click();
  };

  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Element)) return;

    const cardHeart = event.target.closest(CARD_HEART);

    if (cardHeart) {
      event.preventDefault();

      const media = cardHeart.closest(".product-card__media");

      start();
      whenRendered(
        pendingTaps,
        media || cardHeart,
        () => cardButton(media),
        addOnce,
      );
      return;
    }

    const pageHeart = event.target.closest(PAGE_HEART);

    if (pageHeart) {
      start();
      whenRendered(
        pendingTaps,
        pageHeart,
        () =>
          pageHeart.hasAttribute("wk-loaded")
            ? pageHeart.querySelector(".wk-button")
            : null,
        addOnce,
      );
    }
  });

  /* A card's placeholder hides once the app's button is in the card. If it
     still has focus then, keyboard focus would drop to the page, so it is
     handed to the app's button unless the shopper has moved on. */
  document.addEventListener("focusin", (event) => {
    if (!(event.target instanceof Element)) return;

    const cardHeart = event.target.closest(CARD_HEART);

    if (!cardHeart) return;

    const media = cardHeart.closest(".product-card__media");
    const key = media || cardHeart;

    start();

    if (pendingFocus.has(key)) return;

    /* Hiding the placeholder drops its focus to the page, so "the page has
       focus" cannot tell that apart from the shopper moving on. Anything they
       point at or focus elsewhere meanwhile means they have. */
    let movedOn = false;
    const listening = new AbortController();
    const onElsewhere = (other) => {
      if (
        !(other.target instanceof Node) ||
        !cardHeart.contains(other.target)
      ) {
        movedOn = true;
      }
    };

    document.addEventListener("pointerdown", onElsewhere, {
      capture: true,
      signal: listening.signal,
    });
    document.addEventListener("focusin", onElsewhere, {
      capture: true,
      signal: listening.signal,
    });
    window.setTimeout(
      () => listening.abort(),
      REPLAY_TIMEOUT_MS + REPLAY_POLL_MS,
    );

    whenRendered(
      pendingFocus,
      key,
      () => cardButton(media),
      (button) => {
        listening.abort();

        if (movedOn) return;

        const active = document.activeElement;

        if (active === cardHeart || active === document.body || !active) {
          button.focus({ preventScroll: true });
        }
      },
    );
  });

  for (const type of INTERACTION_EVENTS) {
    window.addEventListener(type, start, { capture: true, passive: true });
  }

  document.addEventListener("pointerover", onIntent, { capture: true });
  document.addEventListener("focusin", onIntent, { capture: true });

  /* Runs on the load event or at the cap, whichever comes first. */
  function waitForQuiet() {
    if (quietTimer) return;

    window.clearTimeout(loadCapTimer);
    window.removeEventListener("load", waitForQuiet);

    quietTimer = window.setTimeout(() => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(start, { timeout: IDLE_TIMEOUT_MS });
      } else {
        start();
      }
    }, QUIET_AFTER_LOAD_MS);
  }

  if (document.readyState === "complete") {
    waitForQuiet();
  } else {
    window.addEventListener("load", waitForQuiet, { once: true });
    loadCapTimer = window.setTimeout(waitForQuiet, LOAD_WAIT_CAP_MS);
  }
})();
